import React, { useState } from "react";
import { Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { AiBgPresetSelector, AI_BG_PRESET_KEY, bumpAiBgPresetUsage } from "@/components/AiBgPresetSelector";
import { runAiBgOnCarImages } from "@/lib/aiBgRun";

/**
 * AI háttércsere újrafuttatás — csak hirdetés szerkesztésben (NewListing).
 */
export function AiBgRerunSection({
  carId,
  images,
  originalImages,
  onSuccess,
  testidPrefix = "listing-aibg-rerun",
}) {
  const [fromOriginal, setFromOriginal] = useState(true);
  const [running, setRunning] = useState(false);
  const hasOriginals = Array.isArray(originalImages) && originalImages.length > 0;
  const gallery = (images || []).filter(Boolean);

  const rerun = async () => {
    const useOriginals =
      fromOriginal &&
      hasOriginals &&
      originalImages.length >= gallery.length;
    const source = useOriginals ? originalImages : gallery;
    const urls = (source || []).filter(Boolean);
    if (!urls.length) {
      toast.error("Nincs feldolgozható kép — tölts fel galéria képet.");
      return;
    }
    setRunning(true);
    try {
      bumpAiBgPresetUsage(AI_BG_PRESET_KEY);
      const result = await runAiBgOnCarImages(carId, urls, AI_BG_PRESET_KEY, {
        fromOriginal: useOriginals,
      });
      const processed = result.images || urls;
      const compareOriginals =
        fromOriginal && hasOriginals ? originalImages.filter(Boolean) : urls;
      onSuccess?.({
        processed,
        originals: compareOriginals,
        preset: result.preset || AI_BG_PRESET_KEY,
      });
      toast.success(`AI háttér kész — ${result.processed ?? processed.length} kép`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "AI háttércsere sikertelen");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      data-testid={`${testidPrefix}-block`}
      className="mt-6 glass-strong rounded-2xl p-4 border border-electric/25"
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-electric mb-1">
        <Wand2 className="w-3.5 h-3.5" strokeWidth={1.75} />
        AI háttércsere
      </div>
      <p className="text-[11px] text-slate-400 mb-3">
        Újrafuttatás a jelenlegi galéria képeken. Ha van mentett eredeti, onnan indulhat újra a feldolgozás.
      </p>
      <AiBgPresetSelector testidPrefix={testidPrefix} />
      {hasOriginals && (
        <label
          className="mt-3 flex items-start gap-2 text-xs text-slate-300 cursor-pointer select-none"
          data-testid={`${testidPrefix}-from-original`}
        >
          <input
            type="checkbox"
            className="mt-0.5 rounded border-white/20"
            checked={fromOriginal}
            onChange={(e) => setFromOriginal(e.target.checked)}
            disabled={running}
          />
          <span>
            Eredeti képek használata ({originalImages.length} db) — nem a mostani AI háttér
          </span>
        </label>
      )}
      <button
        type="button"
        data-testid={`${testidPrefix}-run`}
        disabled={running || !gallery.length}
        onClick={rerun}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl bg-electric/90 hover:bg-electric text-white text-sm font-medium disabled:opacity-50"
      >
        {running ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> AI háttércsere folyamatban…
          </>
        ) : (
          <>AI háttércsere újrafuttatása</>
        )}
      </button>
    </div>
  );
}
